import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Area, Bar, CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis, BarChart } from "recharts";
import { AlertTriangle, Clock, Mail, MessageSquareWarning, Phone, PhoneIncoming, Sparkles, Target, TrendingUp, Zap } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fmtDuration, fmtPhone, fmtShort, num, pct } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { EmptyState, KpiCard, LiveTimer, Loading, PageHeader, Pill, RangePicker, ScoreBadge, Section, sentimentTone, severityTone, useRange } from "@/components/common";

function QuickActions() {
  const qc = useQueryClient();
  const run = useMutation({
    mutationFn: (job) => api.post(`/jobs/${job}`).then((r) => r.data),
    onSuccess: (d, job) => {
      toast.success(`${job} finished`, { description: JSON.stringify(d) });
      qc.invalidateQueries();
    },
    onError: (e) => toast.error(errMsg(e)),
  });
  const btn = (job, label, Icon, testId) => (
    <Button key={job} data-testid={testId} size="sm" variant="outline" className="h-8 text-xs border-[#2d3d58] bg-[#0f172a] hover:bg-[#161f30]" disabled={run.isPending} onClick={() => run.mutate(job)}>
      <Icon className="h-3.5 w-3.5 mr-1.5" /> {label}
    </Button>
  );
  return (
    <>
      {btn("process-calls", "Analyze pending calls", Sparkles, "quick-analyze-button")}
      {btn("sms-sla", "Check text SLA", MessageSquareWarning, "quick-sms-button")}
      {btn("anomalies", "Detect anomalies", AlertTriangle, "quick-anomalies-button")}
    </>
  );
}

export default function Overview() {
  const { isAdmin, user } = useAuth();
  const [range, setRange] = useRange(30);
  const params = { date_from: range.from, date_to: range.to };
  const ov = useQuery({ queryKey: ["overview", params], queryFn: () => api.get("/analytics/overview", { params }).then((r) => r.data) });
  const tr = useQuery({ queryKey: ["trends", params], queryFn: () => api.get("/analytics/trends", { params: { ...params, interval: "day" } }).then((r) => r.data) });
  const recent = useQuery({ queryKey: ["recent"], queryFn: () => api.get("/analytics/recent").then((r) => r.data), refetchInterval: 30000 });

  const c = ov.data?.current;
  const p = ov.data?.previous || {};
  const delta = (k) => (c && p[k] != null ? c[k] - p[k] : undefined);

  return (
    <>
      <PageHeader
        eyebrow={isAdmin ? "Mission overview" : `Welcome, ${user?.name?.split(" ")[0] || "agent"}`}
        title={isAdmin ? "Command Center" : "My performance"}
        description={isAdmin ? "Live telemetry across calls, AI coaching, text-response SLAs and unusual activity." : "Your call coaching scores, feedback and text-response queue."}
        actions={
          <>
            <RangePicker range={range} onChange={setRange} />
            {isAdmin && <QuickActions />}
          </>
        }
        testId="overview-header"
      />

      {ov.isLoading ? (
        <Loading />
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3" data-testid="kpi-grid">
          <KpiCard testId="kpi-calls" label="Calls" value={num(c?.calls)} delta={delta("calls")} icon={Phone} sub={`${num(c?.inbound)} in · ${num(c?.outbound)} out`} />
          <KpiCard testId="kpi-analyzed" label="AI reviewed" value={num(c?.analyzed)} delta={delta("analyzed")} icon={Sparkles} tone="violet" sub={`${c?.pending || 0} not yet reviewed`} />
          <KpiCard testId="kpi-avg-score" label="Avg score" value={c?.avg_score ?? "—"} delta={c?.avg_score != null && p.avg_score != null ? c.avg_score - p.avg_score : undefined} icon={Target} tone="emerald" sub={`threshold ${c?.threshold}`} />
          <KpiCard testId="kpi-below" label="Below threshold" value={num(c?.below_threshold)} delta={delta("below_threshold")} invert icon={TrendingUp} tone="red" sub={`${pct(c?.below_threshold, c?.analyzed)}% of reviewed`} />
          <KpiCard testId="kpi-talk" label="Talk time" value={fmtDuration(c?.talk_seconds)} icon={Clock} tone="cyan" sub={`avg ${fmtDuration(c?.avg_duration)} / answered`} />
          <KpiCard testId="kpi-awaiting" label="Texts awaiting" value={num(c?.awaiting_threads)} icon={MessageSquareWarning} tone="amber" sub="clients waiting now" />
          <KpiCard testId="kpi-sms-alerts" label="SLA alerts" value={num(c?.sms_alerts)} delta={delta("sms_alerts")} invert icon={Zap} tone="amber" sub="email + Slack sent" />
          <KpiCard testId="kpi-anomalies" label="Open anomalies" value={num(c?.open_anomalies)} icon={AlertTriangle} tone={c?.open_anomalies ? "red" : "slate"} sub="patterns flagged" />
        </div>
      )}

      <div className="grid xl:grid-cols-3 gap-4">
        <Section title="Call volume & coaching score" eyebrow="Daily trend" className="xl:col-span-2" testId="trend-chart">
          {tr.isLoading ? (
            <Loading />
          ) : tr.data?.series?.length ? (
            <ResponsiveContainer width="100%" height={280}>
              <ComposedChart data={tr.data.series} margin={{ left: -10, right: 10, top: 10 }}>
                <defs>
                  <linearGradient id="gCalls" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.45} />
                    <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#1f293d" vertical={false} />
                <XAxis dataKey="date" tick={{ fill: "#64748b", fontSize: 10, fontFamily: "JetBrains Mono" }} tickFormatter={(d) => d.slice(5)} axisLine={false} tickLine={false} />
                <YAxis yAxisId="l" tick={{ fill: "#64748b", fontSize: 10, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} />
                <YAxis yAxisId="r" orientation="right" domain={[0, 100]} tick={{ fill: "#10b981", fontSize: 10, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ background: "#111827", border: "1px solid #2d3d58" }} labelStyle={{ color: "#94a3b8" }} />
                <Area yAxisId="l" type="monotone" dataKey="calls" stroke="#3b82f6" fill="url(#gCalls)" strokeWidth={2} name="Calls" />
                <Bar yAxisId="l" dataKey="analyzed" fill="#8b5cf6" radius={[3, 3, 0, 0]} name="AI reviewed" barSize={10} />
                <Line yAxisId="r" type="monotone" dataKey="avg_score" stroke="#10b981" strokeWidth={2} dot={{ r: 2 }} connectNulls name="Avg score" />
              </ComposedChart>
            </ResponsiveContainer>
          ) : (
            <EmptyState title="No calls in this range" hint="Run a sync or widen the date range." />
          )}
        </Section>

        <Section title="Score distribution" eyebrow="Reviewed calls" testId="score-distribution">
          {c?.score_distribution?.length ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={c.score_distribution} margin={{ left: -20, right: 0 }}>
                <CartesianGrid stroke="#1f293d" vertical={false} />
                <XAxis dataKey="bucket" tick={{ fill: "#94a3b8", fontSize: 11, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: "#64748b", fontSize: 10 }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "#111827", border: "1px solid #2d3d58" }} />
                <Bar dataKey="count" radius={[4, 4, 0, 0]} fill="#06b6d4" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <EmptyState icon={Sparkles} title="No AI reviews yet" hint={isAdmin ? "Select monitored agents in Settings; calls with transcripts will be scored automatically." : "Your reviewed calls will appear here."} />
          )}
          {c?.call_types?.length > 0 && (
            <div className="mt-4 space-y-1.5">
              <div className="eyebrow">By call type</div>
              {c.call_types.slice(0, 5).map((t) => (
                <div key={t.type} className="flex items-center gap-2 text-xs">
                  <span className="w-20 capitalize text-slate-300">{t.type}</span>
                  <div className="flex-1 h-1.5 bg-[#161f30] rounded">
                    <div className="h-1.5 rounded bg-blue-500" style={{ width: `${pct(t.count, c.analyzed)}%` }} />
                  </div>
                  <span className="font-mono text-slate-400 w-8 text-right">{t.count}</span>
                  <span className="font-mono text-slate-500 w-10 text-right">{t.avg_score ?? "—"}</span>
                </div>
              ))}
            </div>
          )}
        </Section>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <Section title="Recent AI evaluations" eyebrow="Coaching feed" padded={false} testId="recent-evaluations" actions={<Link to="/calls" className="text-xs text-blue-400 hover:text-blue-300">All calls →</Link>}>
          {recent.data?.calls?.length ? (
            <ul className="divide-y divide-[#1f293d]">
              {recent.data.calls.map((call) => (
                <li key={call.id} className="row-hover">
                  <Link to={`/calls?call=${call.id}`} className="flex items-start gap-3 px-4 py-3" data-testid={`recent-call-${call.id}`}>
                    <ScoreBadge score={call.score} size="sm" />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 text-sm">
                        <span className="text-slate-100 font-medium truncate">{call.agent_name}</span>
                        <span className="text-slate-600">·</span>
                        <span className="font-mono text-xs text-slate-400">{fmtPhone(call.client_number)}</span>
                      </div>
                      <div className="text-xs text-slate-400 truncate">{call.topic || call.analysis?.summary}</div>
                      <div className="flex items-center gap-2 mt-1">
                        <Pill tone={sentimentTone(call.sentiment)}>{call.sentiment || "n/a"}</Pill>
                        {call.feedback_email?.status === "sent" && <Pill tone="blue"><Mail className="h-3 w-3" /> emailed{call.feedback_email.cc_applied ? " + cc" : ""}</Pill>}
                        <span className="text-[11px] font-mono text-slate-500 ml-auto">{fmtShort(call.created_at)}</span>
                      </div>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState icon={Sparkles} title="No evaluations yet" hint="Once transcripts arrive from OpenPhone, GPT-5.4 coaching reviews will stream in here." />
          )}
        </Section>

        <Section title="Clients awaiting a text reply" eyebrow="SLA radar" padded={false} testId="awaiting-texts" actions={<Link to="/texts" className="text-xs text-blue-400 hover:text-blue-300">Text SLA →</Link>}>
          {recent.data?.threads?.length ? (
            <ul className="divide-y divide-[#1f293d]">
              {recent.data.threads.map((t) => (
                <li key={t.id} className="row-hover">
                  <Link to={`/texts?thread=${t.id}`} className="flex items-start gap-3 px-4 py-3" data-testid={`awaiting-thread-${t.id}`}>
                    <span className={`mt-1 h-2 w-2 rounded-full ${t.alert_count ? "bg-red-400 pulse-dot text-red-400" : "bg-amber-400"}`} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-mono text-sm text-slate-100">{fmtPhone(t.client_number)}</span>
                        <LiveTimer since={t.awaiting_since} className="text-xs text-amber-300" />
                      </div>
                      <div className="text-xs text-slate-400 truncate">“{t.last_message_text}”</div>
                      <div className="flex items-center gap-2 mt-1 text-[11px]">
                        {t.responsible_name ? <Pill tone="blue">{t.responsible_name}</Pill> : <Pill tone="red">Unassigned → fallback</Pill>}
                        {t.alert_count > 0 && <Pill tone="amber">{t.alert_count} alert{t.alert_count > 1 ? "s" : ""}</Pill>}
                      </div>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState icon={PhoneIncoming} title="Inbox is clear" hint="No client texts are waiting on a reply." />
          )}
        </Section>

        {isAdmin && (
          <Section title="Open anomalies" eyebrow="Pattern detector" padded={false} testId="open-anomalies" actions={<Link to="/anomalies" className="text-xs text-blue-400 hover:text-blue-300">Investigate →</Link>}>
            {recent.data?.anomalies?.length ? (
              <ul className="divide-y divide-[#1f293d]">
                {recent.data.anomalies.map((a) => (
                  <li key={a.id} className="row-hover px-4 py-3 flex items-start gap-3" data-testid={`anomaly-${a.id}`}>
                    <Pill tone={severityTone(a.severity)}>{a.severity}</Pill>
                    <div className="min-w-0">
                      <div className="text-sm text-slate-100">{a.title}</div>
                      <div className="text-xs text-slate-500 mt-0.5 line-clamp-2">{a.description}</div>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <EmptyState icon={AlertTriangle} title="Nothing unusual" hint="Repeated-number, short-call bursts, after-hours and volume spikes are checked hourly." />
            )}
          </Section>
        )}
      </div>
    </>
  );
}
